// Расчёты и формулировки раздела «Деньги». Без React и без fetch — чтобы всё, что экран
// УТВЕРЖДАЕТ, проверялось тестом отдельно от отрисовки.
//
// Файл называется money_report, а не money: `lib/money.ts` уже занят разбором money-полей
// Bitrix («515000|RUB»), это совсем другое, и переименовывать чужой файл ради красоты
// имени нельзя.
//
// ГЛАВНОЕ ПРАВИЛО ЭТОГО РАЗДЕЛА. Любая сумма здесь посчитана по данным, из которых
// ВЫЧЕРКНУТА часть строк (мусор разбора xlsx). Значит, каждая сумма должна приезжать на
// экран вместе с ответом «сколько вычеркнули». Функции ниже устроены так, чтобы забыть
// об этом было трудно: подпись отсечения собирается из тех же данных, что и итог.

import type { Отсечение, СтрокаАгрегата, СтрокаОстатка, СтрокаЧеловека } from '../api/money'
import { fmtMoney } from './format'

/**
 * Оговорка про отчётный месяц. Висит на экране постоянно, а не в подсказке: `ym` пришёл
 * из названия листа xlsx («Июль 26»), и человек, сверяющий эти цифры с бухгалтерией,
 * должен узнать о расхождении ДО того, как начнёт искать ошибку.
 */
export const ПРО_ОТЧЁТНЫЙ_МЕСЯЦ =
  'Месяц здесь — ОТЧЁТНЫЙ, а не календарный: он взят из названия листа в xlsx, который ' +
  'регион сдаёт в центр. С бухгалтерским месяцем он не совпадает, и сумма за «июль» — ' +
  'это то, что регион отнёс к июльскому отчёту, а не то, что произошло в июле.'

/** Вторая постоянная оговорка: это деньги вне расчётного счёта. */
export const ПРО_НАЛИЧНЫЕ =
  'Это касса регионов — наличные и авансовые карты вне расчётного счёта. В Bitrix этих ' +
  'денег нет вовсе, в 1С авансовые отчёты приходят позже и агрегированно: сверять эти ' +
  'суммы с банком и с 1С напрямую нельзя.'

/** Третья: настоящий ФОТ лежит в кассе, а не в таблице ФОТ. */
export const ПРО_ФОТ =
  'Таблица ФОТ в панели пуста. Настоящие зарплатные выплаты лежат в кассе регионов — ' +
  'наличными, по категориям «ФОТ», «Переработки», «Переменная», «Разовые выплаты», «KPI».'

const МЕСЯЦЫ = [
  'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
  'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь',
]

/** «2026-07» → «июль 2026». Не месяц — возвращаем как есть, а не «—»: это значение из данных. */
export function месяцЧеловеку(ym?: string | null): string {
  if (!ym) return '—'
  const m = /^(\d{4})-(\d{2})$/.exec(ym.trim())
  if (!m) return ym
  const n = Number(m[2])
  if (n < 1 || n > 12) return ym
  return `${МЕСЯЦЫ[n - 1]} ${m[1]}`
}

// ─────────── строка запроса ───────────

export interface Фильтры {
  region?: string
  ym?: string
  ym_from?: string
  ym_to?: string
  category?: string
  by?: string
  sort?: string
  limit?: number
  start?: number
  q?: string
  junk?: boolean
  payouts?: boolean
  cap?: number
}

/**
 * Фильтры → строка запроса. Пустые значения не отправляем вовсе: `region=` доехало бы до
 * ядра как «фильтр задан», и ядро честно вернуло бы пусто, а пустая касса читается как
 * «выплат не было».
 */
export function строкаЗапроса(ф: Фильтры): string {
  const p = new URLSearchParams()
  const положить = (к: string, v: unknown) => {
    if (v === undefined || v === null || v === '' || v === false) return
    p.set(к, v === true ? '1' : String(v))
  }
  положить('region', ф.region)
  положить('ym', ф.ym)
  положить('ym_from', ф.ym_from)
  положить('ym_to', ф.ym_to)
  положить('category', ф.category)
  положить('by', ф.by)
  положить('sort', ф.sort)
  положить('q', ф.q)
  положить('cap', ф.cap)
  положить('junk', ф.junk)
  положить('payouts', ф.payouts)
  положить('limit', ф.limit)
  положить('start', ф.start)
  const s = p.toString()
  return s ? `?${s}` : ''
}

// ─────────── отсечение мусора ───────────

/**
 * Одна фраза про отсечение — то, что экран обязан показать рядом с любой суммой.
 *
 * Возвращается ВСЕГДА, в том числе при нуле отсечённых: «ничего не отсечено» — это
 * результат проверки, а молчание читалось бы как «проверки не было».
 */
export function подписьОтсечения(cut?: Отсечение | null): string {
  if (!cut) return 'Отсечение мусора не применялось — суммам ниже верить нельзя.'
  if (!cut.ops) {
    return 'Мусора разбора в этой выборке не нашлось: ни одна операция не отсечена.'
  }
  const части: string[] = []
  if (cut.by_category) части.push(`${cut.by_category} — с числом в поле категории`)
  if (cut.by_amount) части.push(`${cut.by_amount} — с суммой выше порога ${fmtMoney(cut.cap)}`)
  const из = части.length ? ` (${части.join(', ')})` : ''
  // Признаки пересекаются: у части строк съехала и категория, и сумма. Поэтому «из них»,
  // а не «в том числе»: сумма скобок бывает больше общего числа, и это не ошибка.
  const пересечение = cut.by_category + cut.by_amount > cut.ops
    ? ' Признаки пересекаются, поэтому в скобках сумма больше общего числа.'
    : ''
  return (
    `Отсечено ${cut.ops} ${склонение(cut.ops, 'операция', 'операции', 'операций')} ` +
    `на ${fmtMoney(оборот(cut))}${из}.` + пересечение +
    ` Крупнейшая отсечённая сумма — ${fmtMoney(cut.worst)}.`
  )
}

/** Почему отсекаем — одной фразой, для строки под подписью. */
export function правилоОтсечения(cut?: Отсечение | null): string {
  return cut?.rule
    ? `Правило: ${cut.rule}. Дефект в источнике (разбор xlsx сдвинул колонки); зеркало ` +
      'его не чинит — править надо в самой панели.'
    : 'Правило отсечения не сообщено ядром.'
}

/** Отсечение существенно? Ниже этой доли расхода о нём достаточно сказать строкой. */
export const ЗАМЕТНОЕ_ОТСЕЧЕНИЕ = 0.05

export function отсечениеЗаметно(cut: Отсечение | null | undefined, расход: number): boolean {
  if (!cut || !cut.ops) return false
  if (!расход) return true
  return оборот(cut) / Math.abs(расход) >= ЗАМЕТНОЕ_ОТСЕЧЕНИЕ
}

export function склонение(n: number, одна: string, две: string, пять: string): string {
  const a = Math.abs(n) % 100
  const b = a % 10
  if (a > 10 && a < 20) return пять
  if (b > 1 && b < 5) return две
  if (b === 1) return одна
  return пять
}

// ─────────── полнота разреза «по людям» ───────────

export interface ПолнотаПоЛюдям {
  /** Разных получателей с заполненным ФИО. */
  людей: number
  /** Доля выплат, у которых получатель не указан (фамилия внутри комментария). */
  доляБезИмени: number
  /** Готовая фраза для экрана. */
  текст: string
}

/**
 * Насколько разрезу «по людям» можно верить.
 *
 * Половина смысла экрана в этой функции. Получатель заполнен в колонке `fio` не у всех
 * операций; у остальных фамилия внутри комментария, и вытащить её оттуда регулярным
 * выражением нельзя без вранья («Смехун на такси» — это не зарплата). Поэтому список по
 * людям НЕПОЛОН по построению, и экран обязан сказать, насколько именно.
 */
export function полнотаПоЛюдям(итог: {
  ops: number; spent: number; people: number; unnamed_ops: number; unnamed_spent: number
} | null | undefined): ПолнотаПоЛюдям {
  const и = итог || { ops: 0, spent: 0, people: 0, unnamed_ops: 0, unnamed_spent: 0 }
  const доля = и.spent ? и.unnamed_spent / и.spent : 0
  if (!и.unnamed_ops) {
    return {
      людей: и.people,
      доляБезИмени: 0,
      текст: `Получатель указан у всех ${и.ops} выплат — список по людям полон.`,
    }
  }
  return {
    людей: и.people,
    доляБезИмени: доля,
    текст:
      `Список неполон: у ${и.unnamed_ops} ${склонение(и.unnamed_ops, 'выплаты', 'выплат', 'выплат')} ` +
      `на ${fmtMoney(и.unnamed_spent)} (${(доля * 100).toFixed(1)} % суммы) получатель не ` +
      'указан — фамилия осталась внутри комментария. Эти деньги в строке «получатель не ' +
      'указан», а не разложены по людям: угадывать фамилию по комментарию нельзя.',
  }
}

/** Ключ строки для React. Имя может повториться в разных написаниях — добавляем позицию. */
export const ключЧеловека = (r: СтрокаЧеловека, i: number): string => `${i}:${r.person}`

// ─────────── сходимость остатка ───────────

export type Сходимость = 'сошлось' | 'расходится' | 'не с чем'

export interface РазборОстатка {
  вид: Сходимость
  текст: string
  расхождение: number | null
}

/** Копейки округления — не расхождение. Кассу ведут в рублях, порог такой же. */
export const ПОРОГ_СХОДИМОСТИ = 1

/**
 * Сошёлся ли остаток из источника с посчитанным по операциям.
 *
 * Зачем это на экране: остаток нужен именно для того, чтобы поймать ошибку разбора xlsx.
 * Если «начало + приход − расход» не равно «концу» из источника, значит часть строк
 * потерялась или задвоилась — и цифра региона за этот месяц ненадёжна вся целиком.
 */
export function разборОстатка(r: СтрокаОстатка): РазборОстатка {
  if (r.closing === null || r.closing === undefined) {
    return {
      вид: 'не с чем',
      расхождение: null,
      текст: 'Остаток на конец месяца в источнике не заполнен — сверить расчёт не с чем. ' +
             'Это не «сошлось».',
    }
  }
  const d = r.diff ?? r.closing - r.calc_closing
  if (Math.abs(d) <= ПОРОГ_СХОДИМОСТИ) {
    return { вид: 'сошлось', расхождение: d, текст: 'Остаток сходится с реестром операций.' }
  }
  return {
    вид: 'расходится',
    расхождение: d,
    текст:
      `Расхождение ${fmtMoney(d)}: по операциям выходит ${fmtMoney(r.calc_closing)}, ` +
      `в источнике ${fmtMoney(r.closing)}. Цифрам этого региона за этот месяц верить нельзя ` +
      'до разбора в самой панели.',
  }
}

/**
 * Класс значка для сходимости. Берём три состояния дизайн-системы как есть
 * (won/lose/work — см. styles.css §7), своих цветов не вводим: «не с чем сверить» —
 * это именно нейтральное work, а не зелёное «всё хорошо».
 */
export const классСходимости = (вид: Сходимость): string =>
  вид === 'сошлось' ? 'badge won' : вид === 'расходится' ? 'badge lose' : 'badge work'

// ─────────── подписи разрезов и колонок ───────────

export const ПОДПИСЬ_РАЗРЕЗА: Record<string, string> = {
  region: 'Регион',
  ym: 'Отчётный месяц',
  category: 'Категория',
  card: 'Касса / канал',
  kind: 'Вид',
  direction: 'Направление',
}

/** Значение ячейки разреза с человеческим месяцем. */
export function значениеРазреза(r: СтрокаАгрегата, код: string): string {
  const v = (r as unknown as Record<string, unknown>)[код]
  if (v === undefined || v === null || v === '') return '—'
  return код === 'ym' ? месяцЧеловеку(String(v)) : String(v)
}

/** Оборот строки: расход плюс приход. По нему сортируется «где вообще что-то происходит». */
export const оборот = (r: { spent: number; received: number }): number =>
  (r.spent || 0) + (r.received || 0)

/**
 * Доля строки в итоге — для полоски в таблице. Возвращает 0..1.
 * Отрицательный или нулевой итог даёт 0, а не NaN: NaN в ширине полоски ломает вёрстку молча.
 */
export function доля(значение: number, итог: number): number {
  if (!итог || !Number.isFinite(итог) || итог <= 0) return 0
  const d = значение / итог
  return Number.isFinite(d) ? Math.max(0, Math.min(1, d)) : 0
}

/**
 * Регионы кассы, которых нет в справочнике «регион → бизнес-юнит».
 * Фраза, а не список: на этом расхождении имён в exec-панели четыре руководителя из восьми
 * видели нули, и повторять ту же тишину нельзя.
 */
export function проНесведённыеРегионы(список: string[] | undefined): string {
  if (!список || !список.length) return ''
  return (
    `${список.length} ${склонение(список.length, 'регион кассы не сведён', 'региона кассы не сведены', 'регионов кассы не сведены')} ` +
    `с бизнес-юнитом сделок: ${список.join(', ')}. Их деньги видны здесь, но в прибыль ` +
    'подразделения не попадут, пока справочник не дополнят.'
  )
}
